/**
 * InvoiceMergeReview -- one focused review for combining two invoices
 * (SPEC-invoice-merge-history-2026-10-02 R4).
 *
 * Replaces MergeConfirmationDialog's frontend-only rules with the SERVER plan: this component renders
 * exactly what POST /api/invoices/merge-preview returned (lib/invoice-merge-plan.ts) and sends back the
 * choices and acknowledgements the reviewer gave. No lifecycle, payment, survivor or balance decision is
 * made here -- display and selection only.
 *
 * One panel, four sections (Originals, Changes, Payments, Result). Values that are equal on both
 * invoices stay on one compact line; only real conflicts expand into a choice, so the ordinary
 * same-payer unpaid pair needs no field re-entry and no wizard. The existing InvoiceChangeReview's field /
 * provenance language is reused ("Before -> After", client-name sources, "this invoice only"); there is
 * deliberately no shared review framework (needs >= 2 production consumers + characterization tests).
 *
 * Safety properties kept in this file's tests:
 *   * Cancel / Back never sends a write (only previews, which the server guarantees are read-only).
 *   * Combine sends the plan digest the reviewer saw; a changed plan is refused by the server and forces
 *     a fresh review -- never a silent retry with new content (lib/invoice-merge-operation.ts).
 *   * The operation key is persisted: double-submit, response loss, reload and a second tab all reuse it.
 *   * A failed or unrecognised response is never reported as success.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  buildMergeApplyRequest,
  buildMergePdfPreviewRequest,
  mergePlanSchema,
  mergeProjectDefaultsSchema,
  type FutureDefaultField,
  type MergeApplyRequest,
  type MergeApplyResult,
  type MergeChoiceInput,
  type MergeFutureDefaultsRequest,
  type MergePlan,
  type MergePlanField,
  type MergePlanPayment,
  type MergeProjectDefaults,
} from '@/lib/invoice-merge-plan';
import {
  blockingGuidance,
  composeMergeNotice,
  formatMergeMoney,
  interpretMergeError,
  mergeStateLabel,
  type MergeErrorInfo,
} from '@/lib/invoice-merge-guidance';
import {
  hasDurableMergeStorage,
  recoverMergeOperation,
  runMergeApply,
  type MergeApplyOutcome,
} from '@/lib/invoice-merge-operation';
import { Button } from '@/components/ui/button';
import { ReservationReconcileList } from './PaymentReconciliationPanels';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export interface InvoiceMergeReviewProps {
  invoiceIdA: string;
  invoiceIdB: string;
  /** Cancel / close. Sends nothing. */
  onClose: () => void;
  /** Back to choosing the other invoice (only offered when the caller can). Sends nothing. */
  onBack?: () => void;
  /** The merge committed (or an earlier identical request was recovered). */
  onMerged?: (result: MergeApplyResult) => void;
  /** Offer the existing cancel flow for a wrong-project invoice (the server decides when it applies). */
  onCancelInvoice?: (invoiceId: string) => void;
  /** A line shown above everything, e.g. why this panel replaced the old confirmation. */
  notice?: string | null;
}

interface RecipientRow {
  role: 'to' | 'cc';
  email: string;
  name: string;
  contact_id: string | null;
}

const NOTES_FIELDS = new Set(['external_notes', 'internal_notes']);
/**
 * The project-scope defaults the existing selective-change machinery writes and invoice creation reads: the
 * billed client (primary payer, with its DBA), the recipient set, the DBA on its own, the custom billing address
 * and the hide-DBA setting (schemas/invoice_change.py `identity_default`). Anything else -- legal name, PO,
 * notes, dates -- has no project default and is labelled this-invoice-only.
 */
const FUTURE_DEFAULT_OFFERS: FutureDefaultField[] = ['payer', 'recipients', 'billing_address', 'establishment', 'hide_dba'];
const FUTURE_LABELS: Record<FutureDefaultField, string> = {
  payer: 'Billed client',
  recipients: 'TO / CC recipients',
  billing_address: 'Billing address',
  establishment: 'DBA / establishment',
  hide_dba: 'Hide DBA setting',
};
const dateOnly = (iso: string | null | undefined): string => (iso ? iso.slice(0, 10) : '');
const REFRESH_DELAY_MS = 250;
/** The identity and recipient values the review lists with "resulting value + future disposition". */
const IDENTITY_AND_RECIPIENT_FIELDS = [
  'payer',
  'establishment',
  'legal_name',
  'billing_address',
  'hide_dba',
  'suppress_attention_line',
  'recipients',
];

const asRecipients = (value: unknown): RecipientRow[] =>
  Array.isArray(value)
    ? (value as Array<Record<string, unknown> | null>).map((r) => ({
        role: String(r?.role).toLowerCase() === 'to' ? 'to' : 'cc',
        email: String(r?.email ?? ''),
        name: String(r?.name ?? ''),
        contact_id: typeof r?.contact_id === 'string' ? r.contact_id : null,
      }))
    : [];

/** A copy of `record` without `key` (no unused-binding destructuring). */
function without<T>(record: Record<string, T>, key: string): Record<string, T> {
  return Object.fromEntries(Object.entries(record).filter(([k]) => k !== key));
}

const blank = (text: string) => (text.trim() === '' ? '(blank)' : text);

function optionLabel(field: MergePlanField, option: MergePlanField['options'][number], plan: MergePlan): string {
  const survivor = plan.survivor.invoice_number;
  const absorbed = plan.absorbed.invoice_number;
  switch (option.source) {
    case 'survivor':
      return `Invoice ${survivor}: ${blank(option.display)}`;
    case 'absorbed':
      return `Invoice ${absorbed}: ${blank(option.display)}`;
    case 'unset':
      return field.key === 'legal_name'
        ? 'Leave the legal name unset (legal entity not known)'
        : field.key === 'po_number'
          ? 'No PO number'
          : 'Leave unset';
    case 'combined':
      return `Combine both: ${blank(option.display).replace(/\n/g, ' / ')}`;
    case 'edited':
      return 'Enter a different value';
    default:
      return option.display;
  }
}

/** The value a choice would give on the combined invoice, as text (display only). */
function chosenDisplay(field: MergePlanField, choice: MergeChoiceInput | undefined): string | null {
  const source = choice?.source ?? (field.equal ? 'survivor' : null);
  if (!source) return null;
  if (source === 'edited') {
    if (field.kind === 'recipients') {
      return asRecipients(choice?.value).map((r) => `${r.role.toUpperCase()}: ${r.email}`).join(', ') || 'No recipients';
    }
    return blank(String(choice?.value ?? ''));
  }
  const option = field.options.find((o) => o.source === source);
  return option ? blank(option.display) : null;
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section aria-labelledby={`merge-section-${id}`} data-testid={`merge-section-${id}`} className="space-y-2">
      <h3 id={`merge-section-${id}`} className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

function PaymentRow({ payment, plan }: { payment: MergePlanPayment; plan: MergePlan }) {
  const voided = payment.status === 'voided';
  const original = payment.original_invoice_number
    ? `recorded on invoice ${payment.original_invoice_number}`
    : 'original invoice not recorded';
  return (
    <li className="rounded border p-2 text-sm" data-testid={`merge-payment-${payment.id}`}>
      <p className="font-medium">
        {formatMergeMoney(payment.amount)}{' '}
        {voided ? <span className="rounded bg-muted px-1 text-xs">Voided</span> : <span className="text-xs text-muted-foreground">Active</span>}
      </p>
      <p className="text-muted-foreground">
        {payment.paid_at ? `Received ${payment.paid_at.slice(0, 10)} · ` : ''}
        {[payment.method, payment.reference].filter(Boolean).join(' · ') || 'No method or reference recorded'}
        {' · '}
        {original}
        {payment.original_provenance !== 'recorded' && ` (${payment.original_provenance})`}
      </p>
      {voided && (
        <p className="text-muted-foreground">
          Voided{payment.voided_at ? ` ${payment.voided_at.slice(0, 10)}` : ''}
          {payment.void_reason ? `: ${payment.void_reason}` : ''}. Stays voided; it does not return as money received.
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Moves to invoice {plan.survivor.invoice_number} with its payer, reference and history kept.
      </p>
    </li>
  );
}

function RecipientsEditor({
  field,
  choice,
  onChange,
  disabled,
}: {
  field: MergePlanField;
  choice: MergeChoiceInput | undefined;
  onChange: (rows: RecipientRow[]) => void;
  disabled: boolean;
}) {
  const known = useMemo(() => {
    const byEmail = new Map<string, RecipientRow>();
    for (const row of [...asRecipients(field.survivor_value), ...asRecipients(field.absorbed_value)]) {
      if (!byEmail.has(row.email.toLowerCase())) byEmail.set(row.email.toLowerCase(), row);
    }
    return [...byEmail.values()];
  }, [field.survivor_value, field.absorbed_value]);
  const [typed, setTyped] = useState('');
  const current = asRecipients(choice?.value);
  const rows = [...known, ...current.filter((c) => !known.some((k) => k.email.toLowerCase() === c.email.toLowerCase()))];
  const roleOf = (email: string): 'to' | 'cc' | 'none' =>
    current.find((c) => c.email.toLowerCase() === email.toLowerCase())?.role ?? 'none';

  const set = (email: string, role: 'to' | 'cc' | 'none') => {
    const next = rows
      .map((r) => ({ ...r, role: r.email.toLowerCase() === email.toLowerCase() ? role : roleOf(r.email) }))
      .filter((r) => (r.role as string) !== 'none') as RecipientRow[];
    onChange(next);
  };
  const add = () => {
    const email = typed.trim();
    if (!email.includes('@')) return;
    setTyped('');
    onChange([...current, { role: current.some((c) => c.role === 'to') ? 'cc' : 'to', email, name: '', contact_id: null }]);
  };

  return (
    <div className="ml-6 mt-2 space-y-2" data-testid="merge-recipients-editor">
      <p className="text-xs text-muted-foreground">
        Choose who receives this combined invoice. The two invoices' recipients are never merged automatically.
      </p>
      {rows.map((r) => (
        <label key={r.email} className="flex flex-wrap items-center gap-2 text-sm">
          <span className="min-w-0 flex-1 break-all">
            {r.name ? `${r.name} · ` : ''}
            {r.email}
          </span>
          <select
            aria-label={`Role for ${r.email}`}
            className="rounded border p-1"
            disabled={disabled}
            value={roleOf(r.email)}
            onChange={(e) => set(r.email, e.target.value as 'to' | 'cc' | 'none')}
          >
            <option value="to">TO</option>
            <option value="cc">CC</option>
            <option value="none">Not included</option>
          </select>
        </label>
      ))}
      <div className="flex gap-2">
        <input
          aria-label="Add another recipient email"
          className="min-w-0 flex-1 rounded border p-1 text-sm"
          placeholder="name@example.com"
          type="email"
          value={typed}
          disabled={disabled}
          onChange={(e) => setTyped(e.target.value)}
        />
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={add}>
          Add recipient
        </Button>
      </div>
    </div>
  );
}

export function InvoiceMergeReview({
  invoiceIdA,
  invoiceIdB,
  onClose,
  onBack,
  onMerged,
  onCancelInvoice,
  notice = null,
}: InvoiceMergeReviewProps) {
  const queryClient = useQueryClient();
  const [choices, setChoices] = useState<Record<string, MergeChoiceInput>>({});
  const [acknowledged, setAcknowledged] = useState<string[]>([]);
  const [futureFields, setFutureFields] = useState<FutureDefaultField[]>([]);
  const [plan, setPlan] = useState<MergePlan | null>(null);
  const [planKey, setPlanKey] = useState<string | null>(null);
  const [projectDefaults, setProjectDefaults] = useState<MergeProjectDefaults | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<MergeErrorInfo | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [futureError, setFutureError] = useState<string | null>(null);
  /** A future default the server refused: only that choice is unticked, with its own reason. */
  const [futureRefusals, setFutureRefusals] = useState<Partial<Record<FutureDefaultField, string>>>({});
  const [staleNotice, setStaleNotice] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<MergeErrorInfo | null>(null);
  const [unconfirmed, setUnconfirmed] = useState<MergeErrorInfo | null>(null);
  const [committed, setCommitted] = useState<{ result: MergeApplyResult; recovered: boolean } | null>(null);
  const [recovering, setRecovering] = useState(true);
  const [pdf, setPdf] = useState<{
    status: 'idle' | 'loading' | 'ready' | 'error';
    url?: string;
    digest?: string;
    error?: string;
  }>({ status: 'idle' });

  const projectId = projectDefaults?.project_id ?? null;
  const futureRequest: MergeFutureDefaultsRequest | null =
    futureFields.length > 0 && projectId ? { project_id: projectId, fields: futureFields } : null;
  const requestKey = JSON.stringify({ choices, acknowledged: [...acknowledged].sort(), future: futureRequest });
  const seq = useRef(0);
  const firstLoad = useRef(true);
  const latest = useRef({ choices, acknowledged, futureRequest, projectId });
  latest.current = { choices, acknowledged, futureRequest, projectId };

  // ---- recovery: a pending/committed operation for this pair (reload, second tab) ---------------------
  const finishOutcome = useCallback(
    (outcome: MergeApplyOutcome) => {
      if (outcome.kind === 'committed') {
        setCommitted({ result: outcome.result, recovered: outcome.recovered });
        setUnconfirmed(null);
        setSubmitError(null);
        void Promise.all(
          ['invoices', 'invoice-items', 'projects', 'portal'].map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
        );
        onMerged?.(outcome.result);
      } else {
        setAcknowledged([]);
        setReloadToken((n) => n + 1);
        setStaleNotice(
          'An earlier request to combine these invoices did not complete. Nothing was changed. Review the invoices as they are now.',
        );
      }
    },
    [onMerged, queryClient],
  );

  const checkEarlierAttempt = useCallback(async () => {
    setRecovering(true);
    setUnconfirmed(null);
    try {
      const outcome = await recoverMergeOperation(invoiceIdA, invoiceIdB, api.invoices.mergeApply);
      if (outcome) finishOutcome(outcome);
    } catch (error) {
      setUnconfirmed(interpretMergeError(error));
    } finally {
      setRecovering(false);
    }
  }, [finishOutcome, invoiceIdA, invoiceIdB]);

  useEffect(() => {
    void checkEarlierAttempt();
    // once per pair
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceIdA, invoiceIdB]);

  // ---- preview: always read-only; re-run (debounced) whenever the reviewer's selections change --------
  useEffect(() => {
    if (recovering || committed || unconfirmed) return undefined;
    const mine = ++seq.current;
    const delay = firstLoad.current ? 0 : REFRESH_DELAY_MS;
    firstLoad.current = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      const snap = latest.current;
      try {
        const response = await api.invoices.mergePreview({
          invoice_id_a: invoiceIdA,
          invoice_id_b: invoiceIdB,
          choices: snap.choices,
          acknowledged: snap.acknowledged,
          ...(snap.futureRequest ? { future_defaults: snap.futureRequest } : {}),
        });
        if (mine !== seq.current) return;
        const parsed = mergePlanSchema.safeParse((response as { plan?: unknown })?.plan);
        if (!parsed.success) {
          setLoadError({
            ...interpretMergeError(new Error('unrecognised plan')),
            kind: 'unknown',
            message: 'The server returned a review this page does not recognise, so nothing can be combined from here.',
            resolution: 'Reload the page. If it persists, the page and the server are out of step; nothing was changed.',
          });
          setLoading(false);
          return;
        }
        const defaults = mergeProjectDefaultsSchema.safeParse((response as { project_defaults?: unknown })?.project_defaults);
        setPlan(parsed.data);
        setPlanKey(JSON.stringify({ choices: snap.choices, acknowledged: [...snap.acknowledged].sort(), future: snap.futureRequest }));
        setProjectDefaults(defaults.success ? defaults.data : null);
        setLoadError(null);
        setLoading(false);
      } catch (error) {
        if (mine !== seq.current) return;
        const info = interpretMergeError(error);
        if (info.kind === 'invalid_choice' && info.code === 'unknown_acknowledgement' && info.field) {
          setAcknowledged((prior) => prior.filter((code) => code !== info.field));
          return;
        }
        if (info.kind === 'invalid_choice' && info.field && info.field in snap.choices) {
          // The choice no longer fits: drop ONLY that one, say why, and keep every other choice.
          const dropped = info.field;
          setChoices((prior) => without(prior, dropped));
          setFieldErrors((prior) => ({ ...prior, [dropped]: composeMergeNotice(info) }));
          return;
        }
        if (info.kind === 'future_default') {
          const refused = info.futureField as FutureDefaultField | null;
          if (refused && snap.futureRequest?.fields.includes(refused)) {
            // Only THIS choice does not fit. Every other future default the reviewer set stays exactly as set,
            // and the refused one says why instead of vanishing.
            setFutureFields((prior) => prior.filter((f) => f !== refused));
            setFutureRefusals((prior) => ({ ...prior, [refused]: composeMergeNotice(info) }));
            return;
          }
          // Not attributable to one choice (e.g. the project changed): nothing about the future was applied.
          setFutureFields([]);
          setFutureError(`${composeMergeNotice(info)} None of your future-default choices was kept.`);
          return;
        }
        setLoadError(info);
        setLoading(false);
      }
    }, delay);
    return () => clearTimeout(timer);
    // The selections are read through `latest`; `requestKey` re-runs this when they change.
  }, [invoiceIdA, invoiceIdB, requestKey, reloadToken, recovering, committed, unconfirmed]);

  // ---- PDF preview object URL lifecycle -------------------------------------------------------------
  useEffect(() => {
    const url = pdf.url;
    return () => {
      if (url && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
    };
  }, [pdf.url]);

  const fresh = plan !== null && planKey === requestKey && !loading;
  const blocking = plan?.blocking ?? [];
  const canCombine =
    fresh && !!plan && plan.ready_to_apply && blocking.length === 0 && !submitting && !unconfirmed && !committed;

  const setChoice = (key: string, choice: MergeChoiceInput) => {
    setChoices((prior) => ({ ...prior, [key]: choice }));
    // A choice that changed may now be eligible: let the reviewer tick its future default again.
    if ((FUTURE_DEFAULT_OFFERS as string[]).includes(key)) {
      setFutureRefusals((prior) => without(prior as Record<string, string>, key) as Partial<Record<FutureDefaultField, string>>);
    }
    setFieldErrors((prior) => without(prior, key));
    setStaleNotice(null);
    setSubmitError(null);
  };

  const toggleAck = (code: string) => {
    setAcknowledged((prior) => (prior.includes(code) ? prior.filter((c) => c !== code) : [...prior, code]));
    setSubmitError(null);
  };

  const setFuture = (field: FutureDefaultField, on: boolean) => {
    setFutureFields((prior) => {
      const without = prior.filter((f) => f !== field);
      return on ? [...without, field] : without;
    });
    setFutureRefusals((prior) => without(prior as Record<string, string>, field) as Partial<Record<FutureDefaultField, string>>);
    setFutureError(null);
    setSubmitError(null);
  };

  const useSuggestions = () => {
    if (!plan) return;
    setChoices((prior) => {
      const next = { ...prior };
      for (const f of plan.fields) {
        if (!f.equal && !next[f.key] && f.suggested_source && f.suggested_source !== 'edited') {
          next[f.key] = { source: f.suggested_source };
        }
      }
      return next;
    });
  };

  async function combine() {
    if (!plan || !canCombine) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // The operation key is owned by lib/invoice-merge-operation (stored before the request leaves).
      const request: Omit<MergeApplyRequest, 'operation_key'> = without(
        buildMergeApplyRequest(plan, { operationKey: '', acknowledged, futureDefaults: futureRequest }) as unknown as Record<string, unknown>,
        'operation_key',
      ) as unknown as Omit<MergeApplyRequest, 'operation_key'>;
      const outcome = await runMergeApply(request, api.invoices.mergeApply);
      finishOutcome(outcome);
    } catch (error) {
      const info = interpretMergeError(error);
      if (info.kind === 'stale' || (info.kind === 'blocked' && info.definitive)) {
        // The plan changed after it was reviewed: show the new one, keep valid choices, require fresh
        // acknowledgements, and never retry the old content.
        setAcknowledged([]);
        setStaleNotice(composeMergeNotice(info));
        setReloadToken((n) => n + 1);
      } else if (info.definitive) {
        setSubmitError(info);
        if (info.kind === 'retryable') setReloadToken((n) => n + 1);
      } else {
        setUnconfirmed(info);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function previewPdf() {
    if (!plan || !fresh || blocking.length > 0) return;
    setPdf({ status: 'loading' });
    try {
      const out = await api.invoices.mergePreviewPdf(buildMergePdfPreviewRequest(plan, { futureDefaults: futureRequest }));
      if (out.planDigest && out.planDigest !== plan.digest) {
        setPdf({ status: 'error', error: 'The preview was produced for a different review. Nothing is shown; preview again.' });
        return;
      }
      const url = typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(out.blob) : undefined;
      setPdf({ status: 'ready', url, digest: plan.digest });
    } catch (error) {
      const info = interpretMergeError(error);
      if (info.kind === 'stale') {
        setReloadToken((n) => n + 1);
        setStaleNotice(composeMergeNotice(info));
      }
      setPdf({ status: 'error', error: composeMergeNotice(info) });
    }
  }

  // ---------------------------------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------------------------------
  const survivor = plan?.survivor.invoice_number ?? '';
  const absorbed = plan?.absorbed.invoice_number ?? '';
  const conflicts = plan?.fields.filter((f) => !f.equal) ?? [];
  const sameLabels = plan?.fields.filter((f) => f.equal).map((f) => f.label) ?? [];
  const fieldByKey = (key: string) => plan?.fields.find((f) => f.key === key);
  const payerField = fieldByKey('payer');
  const projectName = projectDefaults?.project_name || 'this project';
  const offeredFuture = FUTURE_DEFAULT_OFFERS.filter(
    (offer) => projectDefaults && fieldByKey(offer)?.equal === false,
  );
  const durable = hasDurableMergeStorage();

  const payerScopeId = (() => {
    const resolved = (payerField?.resolved_value ?? payerField?.survivor_value) as { client_scope_id?: string } | null;
    return resolved?.client_scope_id ?? null;
  })();
  const scopeDefaults = projectDefaults?.scopes.find((s) => s.client_scope_id === payerScopeId) ?? null;
  const primaryScope =
    projectDefaults?.scopes.find((s) => s.is_new_invoice_default === true) ?? projectDefaults?.scopes.find((s) => s.is_primary) ?? null;
  // CHR-R5-13: address, DBA, hide-DBA and recipient defaults are saved on the CHOSEN billed client's scope. They reach every
  // new invoice on the project only when that client is (or, with the billed-client default ticked, becomes) the project's
  // default billed client. Otherwise they apply only to invoices billed to that client, and the labels say so.
  const chosenPayerName =
    scopeDefaults?.organization_name ?? (payerField ? chosenDisplay(payerField, choices.payer) : null) ?? 'the chosen billed client';
  const chosenStartsNewInvoices = scopeDefaults ? (scopeDefaults.is_new_invoice_default ?? scopeDefaults.is_primary) : true;
  const defaultsAreNarrow = Boolean(scopeDefaults) && !chosenStartsNewInvoices && !futureFields.includes('payer');
  const narrowScopeText = `invoices billed to ${chosenPayerName} on ${projectName}`;
  const narrowReason = `The project's default billed client stays ${
    primaryScope?.organization_name ?? 'unchanged'
  }, so new invoices keep that client and do not get this default unless you also choose the billed client default.`;
  const isNarrowField = (field: FutureDefaultField): boolean => field !== 'payer' && defaultsAreNarrow;

  const dispositionText = (key: string): string => {
    if (FUTURE_DEFAULT_OFFERS.includes(key as FutureDefaultField)) {
      const field = key as FutureDefaultField;
      if (futureRefusals[field]) return `Not applied as a future default: ${futureRefusals[field]}`;
      if (futureFields.includes(field)) {
        return isNarrowField(field) ? `Future default for ${narrowScopeText} only` : `Future default for ${projectName}`;
      }
      if (field === 'establishment' && futureFields.includes('payer')) {
        return `Future default for ${projectName} (kept with the billed client)`;
      }
      return 'This invoice only (project default unchanged)';
    }
    return 'This invoice only. Future invoices follow the project or client record; change those from the invoice Bill To panel.';
  };

  const futureSummary = (() => {
    if (!plan) return { selected: [] as string[], unchanged: [] as string[], notApplied: [] as string[] };
    const selected = futureFields.map((f) => {
      const fld = fieldByKey(f);
      const display =
        plan.future_defaults?.dispositions.find((d) => d.field === f)?.resolved_display ??
        (fld ? chosenDisplay(fld, choices[f]) : null) ??
        '';
      return `${fld?.label ?? f}: ${display}${isNarrowField(f) ? ` (only ${narrowScopeText})` : ''}`;
    });
    const unchanged = conflicts
      .filter((f) => IDENTITY_AND_RECIPIENT_FIELDS.includes(f.key) && !futureFields.includes(f.key as FutureDefaultField))
      .map((f) => f.label);
    // A future default the reviewer chose that the server could not apply: named here so combining never drops it silently.
    const notApplied = (Object.entries(futureRefusals) as [FutureDefaultField, string][]).map(
      ([field, reason]) => `${FUTURE_LABELS[field]} (${reason})`,
    );
    return { selected, unchanged, notApplied };
  })();

  const body = (() => {
    if (committed) {
      const r = committed.result;
      return (
        <div role="status" data-testid="merge-success" className="space-y-3">
          <h3 className="text-lg font-semibold">Invoices combined</h3>
          {committed.recovered && (
            <p className="rounded bg-blue-50 p-2 text-sm" data-testid="merge-recovered-note">
              This merge was already completed by an earlier request (a lost response, a reload or another tab). This is its
              recorded result; nothing was applied a second time.
            </p>
          )}
          <p>
            Invoice {r.survivor_invoice_number} now holds {r.line_count} line{r.line_count === 1 ? '' : 's'} and{' '}
            {r.payment_count} payment{r.payment_count === 1 ? '' : 's'}. Invoice {r.absorbed_invoice_number} is retired and kept in
            history.
          </p>
          <dl className="grid grid-cols-2 gap-1 text-sm sm:grid-cols-4">
            <div><dt className="text-muted-foreground">Total</dt><dd>{formatMergeMoney(r.money.total)}</dd></div>
            <div><dt className="text-muted-foreground">Received</dt><dd>{formatMergeMoney(r.money.received)}</dd></div>
            <div><dt className="text-muted-foreground">Outstanding</dt><dd>{formatMergeMoney(r.money.outstanding)}</dd></div>
            <div><dt className="text-muted-foreground">Status</dt><dd>{mergeStateLabel(r.result_status)}</dd></div>
          </dl>
          <p className="text-sm text-muted-foreground">
            Nothing was emailed, refunded or moved at the bank. This version of invoice {r.survivor_invoice_number} has not been
            sent; send or download it when you are ready.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <a
              className="inline-flex h-10 items-center justify-center rounded-md border px-4 text-sm font-medium"
              href={`/invoices/${r.survivor_invoice_id}`}
              data-testid="merge-open-result"
            >
              Open invoice {r.survivor_invoice_number}
            </a>
            <Button onClick={onClose} data-testid="merge-done-button">Done</Button>
          </div>
        </div>
      );
    }

    if (recovering) {
      return (
        <p role="status" aria-busy="true" data-testid="merge-recovering">
          Checking whether an earlier merge of these invoices completed…
        </p>
      );
    }

    if (unconfirmed) {
      return (
        <div role="alert" data-testid="merge-unconfirmed" className="space-y-3 rounded border border-amber-400 bg-amber-50 p-3">
          <p className="font-medium">We could not confirm whether the merge completed.</p>
          <p className="text-sm">
            {unconfirmed.resolution} Nothing is assumed either way. Checking again reuses the original request, so it can never
            combine these invoices twice.
          </p>
          {!durable && (
            <p className="text-xs">
              This browser cannot keep a record across a reload; do not close this window until the check succeeds.
            </p>
          )}
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button onClick={() => void checkEarlierAttempt()} data-testid="merge-check-again">Check again</Button>
          </div>
        </div>
      );
    }

    if (!plan) {
      return loadError ? (
        <div role="alert" data-testid="merge-load-error" className="space-y-2">
          <p className="font-medium text-destructive">{loadError.message}</p>
          <p className="text-sm">{loadError.resolution}</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button onClick={() => setReloadToken((n) => n + 1)}>Try again</Button>
          </div>
        </div>
      ) : (
        <p role="status" aria-busy="true" data-testid="merge-loading">Checking these invoices…</p>
      );
    }

    return (
      <div className="space-y-5" aria-busy={loading} data-testid="merge-review-body">
        {notice && <p className="rounded bg-blue-50 p-2 text-sm" data-testid="merge-notice">{notice}</p>}
        {staleNotice && (
          <p role="alert" className="rounded border border-amber-400 bg-amber-50 p-2 text-sm" data-testid="merge-stale-notice">
            {staleNotice}
          </p>
        )}
        {loadError && (
          <p role="alert" className="text-sm text-destructive" data-testid="merge-refresh-error">
            {composeMergeNotice(loadError)}
          </p>
        )}

        {blocking.length > 0 && (
          <div role="alert" className="space-y-2 rounded border border-destructive p-3" data-testid="merge-blocking">
            <p className="font-medium text-destructive">These invoices cannot be combined yet. Nothing was changed.</p>
            {blocking.map((b, i) => {
              const guidance = blockingGuidance(b);
              const target = guidance.openInvoiceId;
              return (
                <div key={`${b.code}-${i}`} data-testid={`merge-blocking-${b.code}`} className="space-y-1 text-sm">
                  <p>{b.message}</p>
                  {b.draw_summary && (
                    <p data-testid="merge-draw-summary">
                      Draw amounts: gross {formatMergeMoney(b.draw_summary.gross)}, holdback {formatMergeMoney(b.draw_summary.holdback)},
                      net {formatMergeMoney(b.draw_summary.net)}. They are left exactly as they are.
                    </p>
                  )}
                  {b.conflicts?.map((c) => (
                    <p key={c.line_id} className="ml-3 text-muted-foreground">
                      Line {c.line_id.slice(0, 8)}: {c.message}
                      {c.other_invoice_number ? ` Already billed on invoice ${c.other_invoice_number}.` : ''}
                    </p>
                  ))}
                  <p className="text-muted-foreground">{guidance.resolution}</p>
                  <div className="flex flex-wrap gap-2">
                    {target && (
                      <a className="text-sm underline" href={`/invoices/${target}`} target="_blank" rel="noopener noreferrer">
                        Open the invoice (new tab)
                      </a>
                    )}
                    {guidance.offerCancel && onCancelInvoice && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => onCancelInvoice(plan.survivor.invoice_id)}>
                          Cancel invoice #{survivor}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => onCancelInvoice(plan.absorbed.invoice_id)}>
                          Cancel invoice #{absorbed}
                        </Button>
                      </>
                    )}
                    {b.retryable && (
                      <Button variant="outline" size="sm" onClick={() => setReloadToken((n) => n + 1)}>Review again</Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ---------------------------------------------------------------- Originals */}
        <Section id="originals" title="Originals">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {plan.sources.map((s) => {
              const iss = plan.issuance.sources.find((i) => i.invoice_id === s.invoice_id);
              const keeps = s.role === 'survivor';
              return (
                <div key={s.invoice_id} className="rounded border p-3 text-sm" data-testid={`merge-original-${s.invoice_number}`}>
                  <p className="font-medium">
                    Invoice {s.invoice_number} · {mergeStateLabel(s.status)}
                  </p>
                  <p className="text-muted-foreground">
                    {s.organization_name ?? 'Client not named'} · {s.line_count} line{s.line_count === 1 ? '' : 's'} ·{' '}
                    {formatMergeMoney(s.lines_total)} · {s.payment_count} payment{s.payment_count === 1 ? '' : 's'}
                  </p>
                  <p data-testid={`merge-original-send-${s.invoice_number}`}>
                    {iss?.ever_sent
                      ? (iss.keystone_send_count ?? 0) > 0
                        ? `Emailed from Keystone${iss.keystone_send_count > 1 ? ` ${iss.keystone_send_count} times` : ''}${
                            iss.last_sent_at ? `, last ${dateOnly(iss.last_sent_at)}` : ''
                          }. That send stays in history.`
                        : `Marked as sent${iss.last_sent_at ? ` on ${dateOnly(iss.last_sent_at)}` : ''} (a person's record; Keystone did not email it). That send stays in history.`
                      : 'No send recorded for this invoice.'}
                    {iss && iss.document_revision_count > 0 &&
                      ` ${iss.document_revision_count} saved PDF cop${iss.document_revision_count === 1 ? 'y' : 'ies'}.`}
                  </p>
                  <p className="font-medium" data-testid={`merge-role-${s.invoice_number}`}>
                    {keeps
                      ? `Keeps its number: ${s.invoice_number} (the lower number always stays)`
                      : `Retired into invoice ${survivor} and kept in history`}
                  </p>
                  <a
                    className="text-sm underline"
                    href={`/invoices/${s.invoice_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`merge-original-link-${s.invoice_number}`}
                  >
                    View original invoice {s.invoice_number} history (opens in a new tab, read-only)
                  </a>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ---------------------------------------------------------------- Changes */}
        <Section id="changes" title="Changes">
          {sameLabels.length > 0 && (
            <p className="text-sm" data-testid="merge-same-fields">
              <span className="font-medium">Same on both invoices:</span> {sameLabels.join(', ')}.
            </p>
          )}
          {conflicts.length === 0 && <p className="text-sm text-muted-foreground">Nothing differs, so there is nothing to choose.</p>}
          {conflicts.some((f) => f.suggested_source && f.suggested_source !== 'edited' && !choices[f.key]) && (
            <Button type="button" variant="outline" size="sm" onClick={useSuggestions} data-testid="merge-use-suggestions">
              Use suggested values
            </Button>
          )}
          {conflicts.map((f) => {
            const selected = choices[f.key]?.source;
            const needsChoice = f.resolution === 'choice_required' && !selected;
            return (
              <fieldset
                key={f.key}
                className="space-y-1 rounded border p-3"
                data-testid={`merge-field-${f.key}`}
                aria-describedby={fieldErrors[f.key] ? `merge-error-${f.key}` : undefined}
              >
                <legend className="px-1 text-sm font-medium">
                  {f.label}{' '}
                  <span className={needsChoice ? 'text-destructive' : 'text-muted-foreground'}>
                    {needsChoice ? '(choose one)' : '(chosen)'}
                  </span>
                </legend>
                {f.key === 'payer' && (
                  <p className="text-xs text-muted-foreground">
                    This sets who the combined invoice is addressed to. Check the DBA, legal name and address below match.
                  </p>
                )}
                {f.key === 'legal_name' && (
                  <p className="text-xs text-muted-foreground">
                    A trading name is not a legal entity. If the legal entity is not known, leave it unset; nothing is guessed.
                  </p>
                )}
                {f.options.map((o) => (
                  <div key={o.source}>
                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="radio"
                        name={`merge-field-${f.key}`}
                        data-testid={`merge-choice-${f.key}-${o.source}`}
                        checked={selected === o.source}
                        disabled={submitting}
                        onChange={() => {
                          if (o.source === 'edited') {
                            const start =
                              f.kind === 'recipients'
                                ? asRecipients(f.survivor_value)
                                : f.kind === 'date'
                                  ? String(f.survivor_value ?? '')
                                  : NOTES_FIELDS.has(f.key)
                                    ? o.display
                                    : '';
                            setChoice(f.key, { source: 'edited', value: start });
                          } else {
                            setChoice(f.key, { source: o.source });
                          }
                        }}
                      />
                      <span>
                        {optionLabel(f, o, plan)}
                        {f.suggested_source === o.source && <span className="ml-1 text-xs text-muted-foreground">(suggested)</span>}
                      </span>
                    </label>
                    {o.source === 'edited' && selected === 'edited' && f.kind === 'recipients' && (
                      <RecipientsEditor
                        field={f}
                        choice={choices[f.key]}
                        disabled={submitting}
                        onChange={(rows) => setChoice(f.key, { source: 'edited', value: rows })}
                      />
                    )}
                    {o.source === 'edited' && selected === 'edited' && f.kind !== 'recipients' && (
                      <div className="ml-6 mt-1">
                        {NOTES_FIELDS.has(f.key) ? (
                          <textarea
                            aria-label={`${f.label} (your wording)`}
                            data-testid={`merge-edit-${f.key}`}
                            className="w-full rounded border p-2 text-sm"
                            rows={3}
                            disabled={submitting}
                            value={String(choices[f.key]?.value ?? '')}
                            onChange={(e) => setChoice(f.key, { source: 'edited', value: e.target.value })}
                          />
                        ) : (
                          <input
                            aria-label={`${f.label} (your value)`}
                            data-testid={`merge-edit-${f.key}`}
                            className="w-full rounded border p-2 text-sm"
                            type={f.kind === 'date' ? 'date' : 'text'}
                            disabled={submitting}
                            value={String(choices[f.key]?.value ?? '')}
                            onChange={(e) => setChoice(f.key, { source: 'edited', value: e.target.value })}
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {fieldErrors[f.key] && (
                  <p id={`merge-error-${f.key}`} role="alert" className="text-sm text-destructive" data-testid={`merge-field-error-${f.key}`}>
                    {fieldErrors[f.key]}
                  </p>
                )}
              </fieldset>
            );
          })}

          {/* ---------------------------------------------- resulting values + future disposition */}
          {conflicts.some((f) => IDENTITY_AND_RECIPIENT_FIELDS.includes(f.key)) && (
            <div className="space-y-2 rounded border p-3" data-testid="merge-future-section">
              <p className="text-sm font-medium">What changes for this invoice, and for future invoices on {projectName}</p>
              <p className="text-xs text-muted-foreground">
                Choosing a value above changes this combined invoice only. Future invoices change only if you select it here; every
                existing invoice on the project stays exactly as it is.
              </p>
              <ul className="space-y-1 text-sm">
                {conflicts
                  .filter((f) => IDENTITY_AND_RECIPIENT_FIELDS.includes(f.key))
                  .map((f) => (
                    <li key={f.key} data-testid={`merge-disposition-${f.key}`}>
                      <span className="font-medium">{f.label}:</span> {chosenDisplay(f, choices[f.key]) ?? 'not chosen yet'} →{' '}
                      <span className="text-muted-foreground">{dispositionText(f.key)}</span>
                    </li>
                  ))}
              </ul>
              {offeredFuture.map((field) => {
                const f = fieldByKey(field)!;
                // The DBA travels with the billed-client default (one write); it is shown as chosen, not as a second choice.
                const withPayer = field === 'establishment' && futureFields.includes('payer');
                const on = futureFields.includes(field) || withPayer;
                return (
                  <fieldset key={field} className="space-y-1 rounded border border-dashed p-2" data-testid={`merge-future-${field}`}>
                    <legend className="px-1 text-sm font-medium">{FUTURE_LABELS[field]} for future invoices</legend>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name={`merge-future-${field}`}
                        data-testid={`merge-future-${field}-pair`}
                        checked={!on}
                        disabled={submitting}
                        onChange={() => setFuture(field, false)}
                      />
                      This pair only
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name={`merge-future-${field}`}
                        data-testid={`merge-future-${field}-project`}
                        checked={on}
                        disabled={submitting || withPayer || f.resolution === 'choice_required'}
                        onChange={() => setFuture(field, true)}
                      />
                      {isNarrowField(field) ? `Use for future ${narrowScopeText}` : `Use for future invoices on ${projectName}`}
                      {withPayer ? ' (kept with the billed client default)' : ''}
                    </label>
                    {isNarrowField(field) && (
                      <p className="text-xs text-muted-foreground" data-testid={`merge-future-${field}-scope`}>
                        This applies only to {narrowScopeText}. {narrowReason}
                      </p>
                    )}
                    {futureRefusals[field] && (
                      <p role="alert" className="text-sm text-destructive" data-testid={`merge-future-${field}-refused`}>
                        Not applied to future invoices: {futureRefusals[field]} Your other future-default choices were kept.
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground" data-testid={`merge-future-${field}-current`}>
                      {field === 'billing_address'
                        ? scopeDefaults?.billing_address_default_source === 'custom' && scopeDefaults.billing_address_default
                          ? `Current project default: ${scopeDefaults.billing_address_default}.`
                          : scopeDefaults?.billing_address_default_source
                            ? `Current project default: the ${scopeDefaults.billing_address_default_source} address.`
                            : 'No project address default is set; new invoices use the client record or the last sent invoice. Choosing this sets the project default to the chosen address.'
                        : field === 'establishment'
                          ? scopeDefaults?.establishment_cleared
                            ? 'The project DBA is explicitly cleared. Choosing a default replaces that.'
                            : scopeDefaults?.establishment_name
                              ? `Current project DBA default: ${scopeDefaults.establishment_name}.`
                              : 'No project DBA default is set.'
                          : field === 'hide_dba'
                            ? scopeDefaults?.hide_dba_default === true
                              ? 'Current project default: DBA hidden on new invoices.'
                              : scopeDefaults?.hide_dba_default === false
                                ? 'Current project default: DBA shown on new invoices.'
                                : 'No hide-DBA default is set; new invoices use the client record.'
                            : field === 'payer'
                        ? primaryScope
                          ? `Current project default: ${primaryScope.organization_name ?? 'client'}${
                              primaryScope.establishment_cleared
                                ? ' (DBA explicitly cleared)'
                                : primaryScope.establishment_name
                                  ? ` · DBA ${primaryScope.establishment_name}`
                                  : ''
                            }.`
                          : 'The project has no explicit default billed client; new invoices use the first client on the project.'
                        : scopeDefaults?.recipients_cleared
                          ? 'The project explicitly has no default recipients (cleared). Choosing a default replaces that.'
                          : scopeDefaults && scopeDefaults.recipients.length > 0
                            ? `Current default recipients: ${scopeDefaults.recipients
                                .map((r) => `${r.role.toUpperCase()} ${r.email}`)
                                .join(', ')}.`
                            : 'No default recipients are set; new invoices use the client’s recipients.'}
                    </p>
                  </fieldset>
                );
              })}
              {projectDefaults && (
                <p className="text-xs text-muted-foreground" data-testid="merge-excluded-invoices">
                  {projectDefaults.excluded_invoice_count === 0
                    ? 'No other invoices exist on this project.'
                    : `${projectDefaults.excluded_invoice_count} other invoice${
                        projectDefaults.excluded_invoice_count === 1 ? '' : 's'
                      } on this project stay unchanged (${projectDefaults.excluded_invoices
                        .slice(0, 8)
                        .map((i) => i.invoice_number)
                        .join(', ')}${projectDefaults.excluded_invoice_count > 8 ? ', …' : ''}).`}
                </p>
              )}
              {futureError && (
                <p role="alert" className="text-sm text-destructive" data-testid="merge-future-error">{futureError}</p>
              )}
            </div>
          )}
        </Section>

        {/* ---------------------------------------------------------------- Payments */}
        <Section id="payments" title="Payments">
          {plan.payments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No payments are recorded on either invoice.</p>
          ) : (
            <ul className="space-y-2">
              {plan.payments.map((p) => (
                <PaymentRow key={p.id} payment={p} plan={plan} />
              ))}
            </ul>
          )}
          {plan.warnings
            .filter((w) => w.severity !== 'info')
            .map((w, i) => (
              <p
                key={`${w.code}-${i}`}
                className={w.severity === 'warning' ? 'rounded bg-amber-50 p-2 text-sm' : 'text-sm text-muted-foreground'}
                data-testid={`merge-warning-${w.code}`}
              >
                {w.message}
              </p>
            ))}
          {plan.acknowledgements.length > 0 && (
            <div className="space-y-2" data-testid="merge-acknowledgements">
              {plan.acknowledgements.map((a) => (
                <label key={a.code} className="flex items-start gap-2 rounded border p-2 text-sm">
                  <Checkbox
                    data-testid={`merge-ack-${a.code}`}
                    checked={acknowledged.includes(a.code)}
                    disabled={submitting}
                    onCheckedChange={() => toggleAck(a.code)}
                  />
                  <span>{a.message}</span>
                </label>
              ))}
            </div>
          )}
        </Section>

        {/* ---------------------------------------------------------------- Result */}
        <Section id="result" title="Result">
          <div className="rounded border p-3 text-sm" data-testid="merge-result">
            <p className="font-medium" data-testid="merge-result-number">
              One invoice: {survivor} ({mergeStateLabel(plan.result.status)})
            </p>
            <dl className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div><dt className="text-muted-foreground">Total</dt><dd data-testid="merge-result-total">{formatMergeMoney(plan.money.total)}</dd></div>
              <div><dt className="text-muted-foreground">Received</dt><dd data-testid="merge-result-received">{formatMergeMoney(plan.money.received)}</dd></div>
              <div><dt className="text-muted-foreground">Outstanding</dt><dd data-testid="merge-result-outstanding">{formatMergeMoney(plan.money.outstanding)}</dd></div>
              {plan.money.excess !== '0.00' && (
                <div><dt className="text-muted-foreground">Excess held</dt><dd data-testid="merge-result-excess">{formatMergeMoney(plan.money.excess)}</dd></div>
              )}
            </dl>
            <p className="mt-2" data-testid="merge-result-issuance">
              This is a new version that has not been sent
              {plan.issuance.result.historical_sends_retained > 0
                ? `; ${plan.issuance.result.historical_sends_retained} earlier send${
                    plan.issuance.result.historical_sends_retained === 1 ? ' stays' : 's stay'
                  } in history`
                : ''}
              . Whether it is paid is separate from whether it was sent.
            </p>
            <details className="mt-2">
              <summary className="cursor-pointer">
                {plan.lines.length} line{plan.lines.length === 1 ? '' : 's'} on the combined invoice
              </summary>
              <ul className="mt-1 space-y-1">
                {plan.lines.map((l) => (
                  <li key={l.id} className="text-muted-foreground">
                    {l.description || 'Line'} · {formatMergeMoney(l.amount)}
                    {l.original_invoice_number ? ` · from invoice ${l.original_invoice_number}` : ''}
                  </li>
                ))}
              </ul>
            </details>
          </div>

          <div className="rounded border p-3 text-sm" data-testid="merge-final-confirmation">
            <p className="font-medium">Before you combine</p>
            <p>
              Invoice {absorbed} is combined into invoice {survivor}. Its lines and payments move onto invoice {survivor}; invoice{' '}
              {absorbed} is retired and stays in history.
            </p>
            {futureSummary.selected.length > 0 ? (
              <p data-testid="merge-final-defaults">
                Future defaults for {projectName}: {futureSummary.selected.join('; ')}.
              </p>
            ) : (
              <p data-testid="merge-final-defaults">No future defaults selected. Future invoices on {projectName} are unchanged.</p>
            )}
            {futureSummary.notApplied.length > 0 && (
              <p className="font-medium text-destructive" data-testid="merge-final-not-applied">
                Not applied as a future default: {futureSummary.notApplied.join('; ')}. These stay this-invoice-only.
              </p>
            )}
            {futureSummary.unchanged.length > 0 && (
              <p data-testid="merge-final-unchanged">Left unchanged for future invoices: {futureSummary.unchanged.join(', ')}.</p>
            )}
            <p className="text-muted-foreground">
              This only combines the records. Nothing is emailed, refunded or moved at the bank, and there is no automatic undo (later
              corrections are ordinary audited edits).
            </p>
          </div>

          {pdf.status !== 'idle' && (
            <div className="space-y-1 rounded border p-3 text-sm" data-testid="merge-pdf-preview">
              {pdf.status === 'loading' && <p role="status">Preparing the preview…</p>}
              {pdf.status === 'error' && <p role="alert" className="text-destructive">{pdf.error}</p>}
              {pdf.status === 'ready' && pdf.digest !== plan.digest && (
                <p role="status" data-testid="merge-pdf-stale">
                  This preview is out of date because your choices changed. Preview again to see the current result.
                </p>
              )}
              {pdf.status === 'ready' && pdf.digest === plan.digest && (
                <>
                  <p className="font-medium">Preview of the combined invoice {survivor} · not sent, not saved</p>
                  <p className="text-xs text-muted-foreground">
                    Invoice {survivor} and invoice {absorbed} stay separate historical documents; open them from their history. This
                    preview is the combined result.
                  </p>
                  {pdf.url ? (
                    <>
                      <iframe title={`Preview of combined invoice ${survivor} (not issued)`} src={pdf.url} className="h-[60vh] w-full rounded border" />
                      <a className="underline" href={pdf.url} target="_blank" rel="noopener noreferrer">Open the preview in a new tab</a>
                    </>
                  ) : (
                    <p>The preview is ready but this browser cannot display it inline.</p>
                  )}
                </>
              )}
            </div>
          )}
        </Section>

        {submitError && (
          <div role="alert" className="rounded border border-destructive p-3 text-sm" data-testid="merge-submit-error">
            <p className="font-medium text-destructive">{submitError.message}</p>
            <p>{submitError.resolution}</p>
            {submitError.missingChoices.length > 0 && <p>Still to choose: {submitError.missingChoices.join(', ')}.</p>}
            {submitError.code === 'checkout_reconciliation_required' && (
              <ReservationReconcileList
                reservations={submitError.reservations}
                onSettled={(result) => {
                  setSubmitError(null);
                  setStaleNotice(`${result.message} Review the invoices again, then combine them.`);
                  setReloadToken((n) => n + 1);
                }}
              />
            )}
          </div>
        )}

        {!durable && (
          <p className="text-xs text-muted-foreground">
            This browser cannot keep a record across a reload. If your connection drops while combining, keep this window open and use
            Check again.
          </p>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="merge-cancel-button" className="w-full sm:w-auto">
            Cancel
          </Button>
          {onBack && (
            <Button variant="outline" onClick={onBack} disabled={submitting} data-testid="merge-back-button" className="w-full sm:w-auto">
              Back
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => void previewPdf()}
            disabled={!fresh || blocking.length > 0 || submitting || plan.missing_choices.length > 0 || pdf.status === 'loading'}
            data-testid="merge-preview-pdf-button"
            className="w-full sm:w-auto"
          >
            Preview combined PDF
          </Button>
          <Button
            onClick={() => void combine()}
            disabled={!canCombine}
            data-testid="merge-combine-button"
            className="w-full sm:w-auto"
          >
            {submitting ? 'Combining…' : `Combine invoice ${absorbed} into ${survivor}`}
          </Button>
        </div>
        {!canCombine && !submitting && plan && (
          <p className="text-right text-xs text-muted-foreground" data-testid="merge-combine-hint">
            {blocking.length > 0
              ? 'Resolve the problem above first.'
              : loading
                ? 'Updating the review…'
                : plan.missing_choices.length > 0
                  ? `Choose a value for: ${plan.missing_choices
                      .map((k) => plan.fields.find((f) => f.key === k)?.label ?? k)
                      .join(', ')}.`
                  : plan.missing_acknowledgements.length > 0
                    ? 'Tick every acknowledgement above to continue.'
                    : ''}
          </p>
        )}
      </div>
    );
  })();

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !submitting) onClose();
      }}
    >
      <DialogContent
        className="max-h-[92dvh] w-[calc(100vw-1rem)] max-w-3xl overflow-y-auto p-4 sm:p-6"
        data-testid="invoice-merge-review"
      >
        <DialogHeader>
          <DialogTitle>
            {plan ? `Review merge: invoice ${absorbed} into invoice ${survivor}` : 'Review merge'}
          </DialogTitle>
          <DialogDescription>
            Combines two invoices into one. Check what changes, what happens to payments and the result before you confirm. Cancelling
            changes nothing.
          </DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}

